'use client';

import { useWorkspaceText } from './workspace/interface-copy';
import {
  letUnplacedProjectGo,
  projectPlace,
  rememberThreadPlace,
} from './workspace/projects/place';
import { attachProject, joinProject, projectHomeId } from './workspace/projects/home';
import { forgetProjects } from './workspace/projects/use-projects';
import { rememberProjectDraft } from './workspace/projects/draft-store';
import type { ProjectPlace } from './workspace/projects/types';

import { BusinessFileAttachmentAdapter } from './lib/business-file-attachments';
import { uploadWorkspaceFile, fileReadabilityMessage } from './workspace/file-upload';
import { StoredChatImageAdapter } from './lib/stored-chat-attachments';
import { MessageRepairProvider } from './lib/message-repair-context';
import type { CompanyFile } from './workspace/types';

import { createResumeCancellation, ResumeCancellationContext } from './lib/resume-cancellation';

import { readLocalModel } from '@/components/model-picker';
import {
  APP_BUILDING_ID,
  APP_AUTO_OPEN_TOOLS,
  appPreviewOpening,
  appPreviewTarget,
} from './workspace/apps/preview/tool-output';
import { readAppBuild } from './workspace/apps/preview/live-build';
import { AppBuildObserver } from './workspace/apps/preview/live-build-observer';
import { announceStorefrontDesign } from './workspace/storefront/storefront-canvas';
import { StorefrontDesignObserver } from './workspace/storefront/storefront-design-observer';
import { isNewAppCanvas } from './workspace/apps/new-app';
import { makingNewAgent } from './workspace/agents/new-agent';

// Compiled to false in every shipping bundle: the dev model override — the
// import above included — exists only in the --dev-tools build (.dev-ui/).
declare const __PONEVA_DEV_TOOLS__: boolean;
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AssistantRuntimeProvider,
  AuiProvider,
  CompositeAttachmentAdapter,
  Suggestions,
  useAui,
  useAuiState,
  useComposerRuntime,
} from '@assistant-ui/react';
import { DownscalingImageAttachmentAdapter } from '@/lib/image-attachments';
import { PastedTextAttachmentAdapter } from '@/lib/pasted-text';
import {
  InlineSizeLimitAdapter,
  PdfAttachmentAdapter,
  inlineAttachmentTooLarge,
} from '@/lib/document-attachments';
import { createResumableSessionStorage, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useInitialServerBranch } from './lib/initial-server-branch';
import { createResilientFetch } from '@/lib/resilient-fetch';
import { SessionChangedNotice } from '@/components/auth/session-changed';
import {
  SessionGuardProvider,
  useSessionBoundFetch,
  useSessionGuard,
} from '@/lib/use-session-guard';
import { SessionChangedError, type SessionGuard } from '@/lib/session-guard';

/** A Chat action ends for good when the session it began under is retired: never resumed or retried. */
const chatTerminalError = (error: unknown) => error instanceof SessionChangedError;
import { createMountResumeStorage, type ResumeStorage } from '@/lib/mount-resume-storage';
import { createEarlyTurnRecovery } from '@/lib/early-turn-recovery';
import { answerResumeApi, discoverAnswerAtMount, admittedAnswerStorage } from '@/lib/answer-resume';
import { ChatCheckAgainContext, markUnconfirmed } from '@/lib/chat-check-again';
import {
  createFollowFetch,
  createFollowStorage,
  followUrl,
  takeFollowScroll,
  useChatFollow,
  type FollowOutcome,
} from '@/lib/chat-follow';
import { FollowMerge } from '@/lib/chat-follow-merge';
import { createSubmittedTurnHistory } from '@/lib/submitted-turn-history';
import { WorkspaceChatTransport, type ToolResultOrigin } from '@/lib/workspace-chat-transport';
import { finishPreviewContext } from '@/lib/workspace-turn-state';
import { traceHistory } from '@/lib/history-trace';
import { completeWorkspaceTurn } from '@/lib/workspace-turn-completion';
import { ActionTurnObserver } from '@/components/assistant-ui/action-turn-observer';
import { ActivityScopeObserver } from '@/components/assistant-ui/activity-store';
import {
  collaborationThreadComponents,
  WorkspaceProvider,
  WorkspaceShell,
  WorkspaceWelcome,
  WorkspaceWelcomeFooter,
  WorkspaceWelcomeSuggestion,
  askedSuggestionBody,
  captureModelSelection,
  useOptionalWorkspace,
} from '@/workspace';
import type { CompanySkill } from '@/workspace';
import { SavedArtifactCanvasRenderer } from '@/workspace/artifacts/saved-canvas-renderer';
import { voicePreviewTarget } from '@/workspace';
import { Thread } from '@/components/assistant-ui/thread';
import { ChatI18nProvider } from '@/lib/chat-i18n';
import { resolveAppLocales } from '@/lib/app-locales';
import { ComposerHighlightsContext, type ComposerHighlight } from '@/lib/composer-highlights';
import { AuthGateContext } from '@/auth-gate';
import { BillingContext, useBilling, useBillingState, useQuestionGate } from '@/billing/context';
import { CompanyBillingDialog } from '@/company/billing-dialog';
import { consumeBillingDraft, stashBillingDraft, billingDraftForUser } from '@/billing/draft';
import { createBillingFetch } from '@/billing/api';
import { guardSubmittedTurn } from '@/billing/submit-guard';
import { AuthScreen } from '@/components/auth/auth-screen';
import { consumeAuthFragment } from '@/components/auth/auth-fragment';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CompanySettingsPage } from '@/company/settings-page';
import { ConnectionReturn } from '@/company/connection-return';
import { chatHash, keepsLinkedChatOnSignIn, writeWorkspaceHash } from '@/lib/chat-links';
import { committedSaveTarget } from '@/workspace/artifacts/chat-save-receipt';
import { notifyLibraryChanged } from '@/workspace/artifacts/library-changed';
import { useChatNavigation } from '@/lib/use-chat-navigation';
import { createSessionBoundary } from '@/lib/session-boundary';
import { purgeRememberedDictation, rememberedOwnerToPurge } from '@/lib/session-owner-cleanup.mjs';
import { CompanyWelcome } from '@/company/welcome';
import { CompanyChatSurface, CompanyProvider, useCompany } from '@/company/context';
import { type Settings, EMPTY_SETTINGS, fetchServerSettings } from '@/settings';
import { loadThemeChoice, resolveTheme, systemPrefersDark, useThemeChoice } from '@/lib/theme';
import {
  routeSettingsArtifact,
  useAccountSettingsControl,
  type SettingsChange,
} from '@/lib/account-settings';
import { type ThreadMeta, newThreadId, withThreadTitle } from '@/thread-types';
import {
  type AuthConfig,
  type SessionUser,
  fetchAuthConfig,
  fetchConfig,
  fetchMe,
  beginGoogleSignIn,
  beginNativeGoogleSignIn,
  completeNativeGoogleSignIn,
  completePasswordSignup,
  resumeOAuthSession,
  AuthError,
  logout as serverLogout,
  listServerThreads,
  loadServerThreadMessages,
  seedServerThread,
  mirrorThreadTitle,
} from '@/server-threads';
import { apiUrl, authHeaders, initSessionToken, SessionTokenKeptError } from '@/lib/shell';
import {
  clearNativeGoogleVerifier,
  getNativeGoogleLaunchCallback,
  isAndroidNativeShell,
  listenForNativeGoogleCallback,
  oauthProvidersForCurrentPlatform,
  openNativeGoogleSignIn,
  prepareNativeGoogleSignIn,
} from '@/lib/native-google-oauth';
import { useKeyboardInset, useNarrowViewport } from '@/lib/mobile';
import { desktopBridge } from '@/lib/desktop';
import { rememberMessageContext } from '@/lib/message-context';
import { turnContextChips } from '@/workspace/message-context-chips';
import { StarterSuggestionsContext, useStarterSuggestions } from '@/lib/starter-suggestions';
import { WorkspaceStarterHeading } from '@/workspace/home-actions';
import { SPEECH_UNAVAILABLE, type SpeechAvailability } from '@/speech/contracts';
import { speechSpool } from '@/speech/spool';
import { SpeechProvider } from '@/speech/ui/speech-provider';
import { chatRequestBody } from '@/lib/chat-request-body';
import { liveAnswer, publishingResumeStorage } from '@/voice/live-answer';
import { SteerProvider } from '@/lib/steer/use-steer';
import { queuedOriginBody } from '@/lib/steer/origin';
import { rememberStoppedStream, replaceStreamBody } from '@/lib/steer/replace';
import { plainStreamId } from '@/lib/steer/stream-id';
import { currentVoiceTurnBody } from '@/voice/turn-body';
import { VoiceRoot } from '@/voice/voice-root';
import { voiceController } from '@/voice/runtime';
import {
  meetingRecordingInProgress,
  releaseSignedOutSpeech,
  SignOutWhileRecordingDialog,
} from '@/workspace/meeting-sign-out';
import { resumeOAuthRequest, stashOAuthRequest } from '@/lib/oauth-return';
import { useGoogleSignInAttempt } from '@/lib/use-google-sign-in-attempt';
import { announceKnowledgePagesChanged } from '@/workspace/knowledge-agent-events';
import { suggestionCanvas } from '@/workspace/knowledge-pages/suggestion-link';
import { WorkspaceNativeTool } from '@/workspace/native-tool';

// ---------------------------------------------------------------------------
// Theme: light, dark or system (ui/lib/theme.ts). localStorage is the
// pre-paint cache; the choice is also a server setting.
// Theme default is LIGHT; the "dark" class (root div + <html>) drives dark.

// ── Auth boot params + OAuth redirect continuity ─────────────────────────────
// The Google flow is a full-page redirect, so two one-shot channels carry
// state across it: URL params on the way back (?auth_error=<key>, and the
// ?invite= deep link stashed to sessionStorage so a code survives the round-trip
// and never lingers in the address bar), and a sessionStorage draft of the
// composer text captured just before leaving (the SPA's in-memory composer is
// lost when the browser navigates away).

const INVITE_KEY = 'poneva.invite';
const OAUTH_DRAFT_KEY = 'poneva.oauthDraft';
const LAST_SESSION_OWNER_KEY = 'poneva.lastSessionOwner';

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* best effort */
  }
}

function consumeBootParams(): {
  authError: string | null;
  authComplete: string | null;
  invite: string | null;
  verify: string | null;
  reset: string | null;
} {
  let authError: string | null = null;
  let authComplete: string | null = null;
  let inviteParam: string | null = null;
  let params: URLSearchParams | null = null;
  try {
    params = new URLSearchParams(location.search);
    authError = params.get('auth_error');
    authComplete = params.get('auth_complete');
    inviteParam = params.get('invite');
  } catch {
    /* URL APIs unavailable */
  }
  if (inviteParam) {
    try {
      sessionStorage.setItem(INVITE_KEY, inviteParam);
    } catch {
      /* storage disabled — retain the in-memory fallback below */
    }
  }
  if (params && (authError || authComplete || inviteParam)) {
    try {
      params.delete('auth_error');
      params.delete('auth_complete');
      params.delete('invite');
      const qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : '') + location.hash);
    } catch {
      /* history unavailable — the in-memory invite still works */
    }
  }
  let invite: string | null = inviteParam;
  try {
    invite = sessionStorage.getItem(INVITE_KEY) ?? invite;
  } catch {
    /* storage disabled */
  }
  return {
    authError,
    authComplete,
    invite,
    verify: consumeAuthFragment('verify'),
    reset: consumeAuthFragment('reset'),
  };
}

function clearInvite() {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    /* storage disabled */
  }
}

function consumeOAuthDraft(): { text: string; send: boolean } | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_DRAFT_KEY);
    sessionStorage.removeItem(OAUTH_DRAFT_KEY);
    return raw ? (JSON.parse(raw) as { text: string; send: boolean }) : null;
  } catch {
    return null;
  }
}

function stashOAuthDraft(text: string, send: boolean) {
  try {
    if (text.trim()) sessionStorage.setItem(OAUTH_DRAFT_KEY, JSON.stringify({ text, send }));
  } catch {
    /* best effort — worst case the user retypes */
  }
}

/** How a pane takes part in a shared chat (collaboration v1): a reader, and
 * whether it follows someone's running turn, and where it opens. */
type PaneShare = { readOnly: boolean; following: boolean; keepScrollTop: number | null };

function ThreadWithSuggestions({
  threadId,
  replyLocale,
  composerHighlights,
  share,
}: {
  threadId: string;
  replyLocale: string;
  composerHighlights: readonly ComposerHighlight[];
  share: PaneShare;
}) {
  // Starting points follow the intent of the conversation being started, so the
  // welcome of a new Skill offers Skills worth adding instead of company
  // questions. Clicking one sends it through the composer, and the transport
  // reads this same turn context, so the authoring intent and canvas are kept.
  const workspace = useOptionalWorkspace();
  const company = useCompany();
  const empty = useAuiState((state) => state.thread.messages.length === 0);
  // A new App's chat (apps/new-app.ts) offers its App ideas instead (the welcome footer), not company questions;
  // a new agent or Storefront design chat starts from the person's own description.
  // A project's home shows the work in the project, not company starting points.
  const focusedEntry =
    isNewAppCanvas(workspace?.canvas) ||
    Boolean(workspace && (makingNewAgent(workspace.turnContext) || projectHomeId(workspace))) ||
    workspace?.canvas?.kind === 'storefront';
  const { suggestions, suggestionKey, ...starters } = useStarterSuggestions({
    threadId,
    locale: replyLocale,
    intent: workspace?.turnContext.intent,
    scope: `${workspace?.user?.id ?? ''}:${company.data?.company?.id ?? ''}`,
    enabled: Boolean(company.ready && workspace?.view === 'chat' && empty && !focusedEntry),
  });
  return (
    <StarterSuggestionsContext.Provider value={starters}>
      <ThreadSuggestionRuntime
        suggestionKey={suggestionKey}
        suggestions={suggestions}
        composerHighlights={composerHighlights}
        share={share}
      />
    </StarterSuggestionsContext.Provider>
  );
}

function ThreadSuggestionRuntime({
  suggestions,
  suggestionKey,
  composerHighlights,
  share,
}: {
  suggestions: ReturnType<typeof useStarterSuggestions>['suggestions'];
  suggestionKey: string;
  composerHighlights: readonly ComposerHighlight[];
  share: PaneShare;
}) {
  const workspace = useOptionalWorkspace();
  // Suggestions initializes its own resource once. Replace that resource when
  // the set changes, while keeping the thread and any held voice control mounted.
  const aui = useAui({ suggestions: { ...Suggestions(suggestions), key: suggestionKey } });
  return (
    <AuiProvider value={aui}>
      <ComposerHighlightsContext.Provider value={composerHighlights}>
        <Thread
          components={
            workspace
              ? {
                  Welcome: WorkspaceWelcome,
                  SuggestionContent: WorkspaceWelcomeSuggestion,
                  SuggestionsHeader: WorkspaceStarterHeading,
                  ToolFallback: WorkspaceNativeTool,
                  // The composer dock holds the owner's suggested follow-ups
                  // and the Questions panel, which docks above the composer,
                  // never in the transcript (#2732). Collaboration v1 also
                  // adds "Suggested by" from the server, Fork from here, and
                  // for a follower the live line and a read-only bar.
                  ...collaborationThreadComponents(share),
                  WelcomeFooter: WorkspaceWelcomeFooter,
                }
              : undefined
          }
        />
      </ComposerHighlightsContext.Provider>
    </AuiProvider>
  );
}

// Sends the message the user typed before being asked to sign in, once they
// are signed in. The composer keeps its text across the auth flow (ChatPane is
// not remounted on sign-in), so we just fire its send. Rendered inside the
// runtime provider so useComposerRuntime resolves the thread composer.
function PendingAuthSend({ armed, onConsumed }: { armed: boolean; onConsumed: () => void }) {
  const composer = useComposerRuntime();
  const gate = useQuestionGate();
  const company = useCompany();
  const billing = useBilling();
  // Latch so we fire exactly once per arming: onConsumed's identity changes on
  // every render, and an unrelated async setState (e.g. server settings landing
  // after sign-in) could otherwise re-run this effect while `armed` is still
  // true and send the message twice. Reset when disarmed.
  const firedRef = useRef(false);
  useEffect(() => {
    if (!armed) {
      firedRef.current = false;
      return;
    }
    if (firedRef.current) return;
    // Authentication can complete before the company and billing providers
    // have adopted the new account. Keep the typed turn armed until both
    // boundaries are known; otherwise the fail-closed gate would consume it
    // without dispatching anything.
    if (!company.ready || billing?.required === null) return;
    firedRef.current = true;
    if (composer.getState().text.trim() && gate.allow()) composer.send();
    onConsumed();
  }, [armed, composer, company.ready, billing?.required, gate, onConsumed]);
  return null;
}

// Restores the composer draft stashed before a Google full-page redirect (the
// SPA's in-memory composer text dies when the browser leaves for the consent
// screen). Waits for the boot session resolution: signed in → restore AND send
// (completing the pre-redirect pending send); not signed in (OAuth failed or
// was cancelled) → just restore the text. Each draft is consumed once; later
// Settings guidance creates a new draft even when its text is identical.
function ComposerResume({
  draft,
  sessionResolved,
  signedIn,
  billingReady,
  onConsumed,
}: {
  draft: { text: string; send: boolean } | null;
  sessionResolved: boolean;
  signedIn: boolean;
  billingReady: boolean;
  onConsumed: () => void;
}) {
  const composer = useComposerRuntime();
  const gate = useQuestionGate();
  const company = useCompany();
  const consumedDraft = useRef<typeof draft>(null);
  useEffect(() => {
    if (!draft || !sessionResolved || consumedDraft.current === draft) return;
    // A redirect can resolve auth before the company and billing providers.
    // Do not consume an auto-send while either gate is still unknown: the
    // draft must survive until a definitive allow/reject decision exists.
    if (draft.send && signedIn && (!company.ready || !billingReady)) return;
    consumedDraft.current = draft;
    composer.setText(draft.text);
    if (draft.send && signedIn && gate.allow()) composer.send();
    onConsumed();
  }, [draft, sessionResolved, signedIn, billingReady, company.ready, composer, onConsumed]);
  return null;
}

// Hands App a way to read the current composer text (for the pre-OAuth draft
// stash) without lifting composer state out of the runtime provider.
function ComposerTextBridge({
  register,
  registerAttachments,
}: {
  register: (fn: () => string) => void;
  registerAttachments: (fn: () => boolean) => void;
}) {
  const composer = useComposerRuntime();
  useEffect(() => {
    register(() => composer.getState().text);
    registerAttachments(() => composer.getState().attachments.length > 0);
  }, [composer, register, registerAttachments]);
  return null;
}

function ChatPane({
  chatAttachments,
  selected,
  composerHighlights,
  answerReaderAdmitted = false,
  threadId,
  readOnly,
  following,
  onFollowOutcome,
  onFollowRetryAfter,
  signedIn,
  isCurrentHistoryOwner,
  settings,
  replyLocale,
  onHistoryChange,
  onThreadSaved,
  onThreadTitle,
  pendingSend,
  onPendingSendConsumed,
  resumeDraft,
  sessionResolved,
  onResumeConsumed,
  registerComposerText,
  registerComposerAttachments,
  speech,
  userId,
  onSettingsChanged,
}: {
  chatAttachments: boolean;
  selected: Set<string>;
  composerHighlights: readonly ComposerHighlight[];
  answerReaderAdmitted?: boolean;
  threadId: string;
  /** Someone else's shared chat (collaboration v1): read, never write. */
  readOnly: boolean;
  /** This pane follows someone's running turn instead of sending one: a
   * reader, or the owner watching their own agent run (lib/chat-follow.ts). */
  following: boolean;
  onFollowOutcome: (outcome: FollowOutcome) => void;
  onFollowRetryAfter: (seconds: number) => void;
  signedIn: boolean;
  isCurrentHistoryOwner: () => boolean;
  settings: Settings;
  replyLocale: string;
  onHistoryChange: () => void;
  onThreadSaved: (id: string) => void;
  onThreadTitle: (threadId: string, title: string) => void;
  pendingSend: boolean;
  onPendingSendConsumed: () => void;
  resumeDraft: { text: string; send: boolean } | null;
  sessionResolved: boolean;
  onResumeConsumed: () => void;
  registerComposerText: (fn: () => string) => void;
  registerComposerAttachments: (fn: () => boolean) => void;
  speech: SpeechAvailability;
  userId: string | null;
  /** A live update_my_settings result: App applies it at once when it is this account's. */
  onSettingsChanged: (change: SettingsChange, origin: ToolResultOrigin) => void;
}) {
  const { text: uiText } = useWorkspaceText();
  const workspace = useOptionalWorkspace();
  const company = useCompany();
  // Read through a ref so a new callback never rebuilds the transport.
  const settingsChangedRef = useRef(onSettingsChanged);
  settingsChangedRef.current = onSettingsChanged;
  const workspaceRef = useRef(workspace);
  // Threads that have already shown an App preview in this page session: the first one opens the App at once.
  const appPreviewShown = useRef(new Set<string>());
  workspaceRef.current = workspace;
  const visibleMount = useRef(true);
  useEffect(() => {
    visibleMount.current = true;
    return () => {
      visibleMount.current = false;
    };
  }, []);
  // Ride the composer above the iOS soft keyboard (Android resizes natively).
  useKeyboardInset();
  const narrowViewport = useNarrowViewport();

  const questionGate = useQuestionGate();
  const billing = useBilling();
  const billingGuardRef = useRef({ questionGate, billing });
  billingGuardRef.current = { questionGate, billing };

  // A follower writes nothing into the chat: no history, no title, no
  // workspace events, and its stream id lives only in memory.
  const passive = readOnly || following;
  const followOutcomeRef = useRef(onFollowOutcome);
  followOutcomeRef.current = onFollowOutcome;
  const followRetryRef = useRef(onFollowRetryAfter);
  followRetryRef.current = onFollowRetryAfter;
  // A follower who had scrolled up keeps their place when the pane remounts
  // to attach to a new turn.
  const [keepScrollTop] = useState(() => (following ? takeFollowScroll(threadId) : null));
  const share = useMemo<PaneShare>(
    () => ({ readOnly, following, keepScrollTop }),
    [readOnly, following, keepScrollTop],
  );
  const readerAdmissionRef = useRef(answerReaderAdmitted);
  readerAdmissionRef.current = answerReaderAdmitted;
  const resumeStorage = useMemo<ResumeStorage>(
    () =>
      admittedAnswerStorage(
        following
          ? createFollowStorage(threadId)
          : createMountResumeStorage(
              publishingResumeStorage(
                createResumableSessionStorage({ key: `poneva.chat.resume.${threadId}` }),
                threadId,
              ),
            ),
        () => readerAdmissionRef.current,
      ),
    [threadId, following],
  );
  const resumeCancellation = useMemo(() => createResumeCancellation(), [threadId]);
  // Every person-initiated stop (Stop, Esc, Send now, voice) remembers the stopped
  // stream, so the next send can name it (CONTRACT §5a).
  const cancelForStop = useCallback(() => {
    const live = liveAnswer.current();
    const stopped = live?.threadId === threadId ? plainStreamId(live.streamId) : null;
    if (stopped) rememberStoppedStream(threadId, stopped);
    resumeCancellation.cancel();
  }, [resumeCancellation, threadId]);
  const historyChangeRef = useRef(onHistoryChange);
  historyChangeRef.current = onHistoryChange;
  const threadSavedRef = useRef(onThreadSaved);
  threadSavedRef.current = onThreadSaved;
  // The visible adapter can be replaced during login/logout while its SDK
  // request continues. Keep that run bound to the owner captured at dispatch.
  const requestHistoryOwnerRef = useRef(isCurrentHistoryOwner);
  // A pane mounted before /me answered (a deep link right after login) loads its history once the session is known.
  const sessionKnown = useRef<{
    promise: Promise<boolean>;
    resolve: (signedIn: boolean) => void;
  } | null>(null);
  if (!sessionKnown.current) {
    let resolve!: (signedIn: boolean) => void;
    const promise = new Promise<boolean>((done) => (resolve = done));
    sessionKnown.current = { promise, resolve };
  }
  useEffect(() => {
    if (sessionResolved) sessionKnown.current?.resolve(signedIn);
  }, [sessionResolved, signedIn]);
  // This chat's turns the transport refused before sending: their messages are never written to history.
  const unsentTurns = useMemo(() => new Set<string>(), [threadId]);
  const { history, rememberSubmittedTurn, rememberFinishedTurn } = useMemo(
    () =>
      createSubmittedTurnHistory(threadId, {
        signedIn,
        isUnsent: (messageId) => unsentTurns.has(messageId),
        attachmentReferences: chatAttachments && signedIn,
        whenSessionKnown: sessionKnown.current?.promise,
        onLoadedMessages: (messages) => {
          if (!passive)
            discoverAnswerAtMount(resumeStorage, threadId, messages, answerReaderAdmitted);
        },
        readOnly: passive,
        isCurrentOwner: () => requestHistoryOwnerRef.current(),
        onHistoryChange: () => historyChangeRef.current(),
      }),
    [
      unsentTurns,
      threadId,
      signedIn,
      passive,
      isCurrentHistoryOwner,
      resumeStorage,
      answerReaderAdmitted,
      chatAttachments,
    ],
  );
  const finishedTurnRef = useRef(rememberFinishedTurn);
  // Bound to the guard of the identity this pane was built under; a replacement guard is never followed.
  const chatFetch = useSessionBoundFetch();
  const transport = useMemo(() => {
    // Resumable streams (the iOS "Load failed" fix). The storage key is
    // thread-scoped so a mount-time resume can never graft another thread's
    // interrupted turn into this one (the runtime remounts per thread).
    // createResilientFetch re-attaches transparently on mid-stream drops;
    // the sessionStorage id additionally covers reload/relaunch resume.
    // A follower attaches through the follow route (`follow:<threadId>` at
    // mount), re-attaches with ?stream=, and never stops the owner's turn.
    const resumeApi = following
      ? () => followUrl(threadId)
      : answerResumeApi(
          threadId,
          apiUrl,
          (streamId: string) => apiUrl(`/api/chat/resume/${streamId}`),
          answerReaderAdmitted,
        );
    const recovery = createEarlyTurnRecovery({
      // A follower never submits here, so there is no early turn to recover.
      enabled: signedIn && !passive,
      threadId,
      storage: resumeStorage,
      resumeApi,
      recoveryApi: ({ threadId: recoveryThread, messageId, streamId }) =>
        apiUrl(
          `/api/chat/recover/${encodeURIComponent(recoveryThread)}/${encodeURIComponent(messageId)}/${encodeURIComponent(streamId)}`,
        ),
      begin: resumeCancellation.begin,
      mayStopRun: resumeCancellation.stoppedSince,
      onUnconfirmed: (binding) => {
        if (userId && requestHistoryOwnerRef.current())
          markUnconfirmed(userId, binding.threadId, {
            messageId: binding.messageId,
            assistantCount: null,
          });
      },
      baseFetch: following
        ? createFollowFetch({
            baseFetch: chatFetch,
            threadId,
            answerReaderAdmitted,
            onOutcome: (outcome) => followOutcomeRef.current(outcome),
            onRetryAfter: (seconds) => followRetryRef.current(seconds),
          })
        : createResilientFetch({
            baseFetch: chatFetch,
            terminalError: chatTerminalError,
            resumeUrl: resumeApi,
            headers: () => authHeaders(),
            // Only the person's Stop forgets the stream; a detach keeps it for the next mount to re-attach.
            onAborted: (streamId, since) =>
              setTimeout(() => {
                if (!resumeCancellation.stoppedSince(since)) return;
                // Forget only THIS stream, and only while it is still the stored one.
                if (resumeStorage.clearIfCurrent) resumeStorage.clearIfCurrent(streamId);
                else resumeStorage.clear();
              }, 0),
            begin: resumeCancellation.begin,
            mayStopRun: resumeCancellation.stoppedSince,
          }),
    });
    return new WorkspaceChatTransport(
      {
        attachmentReferences: chatAttachments && signedIn && !passive,
        onThreadTitle: (event) => {
          if (passive || event.threadId !== threadId || !requestHistoryOwnerRef.current()) return;
          if (signedIn) mirrorThreadTitle(threadId, event.title);
          onThreadTitle(threadId, event.title);
        },
        // apiUrl: absolute origin in the native shells, same-origin on web.
        // headers is resolved per request so a login mid-session takes effect
        // without remounting the transport (bearer auth for the shells; empty
        // on web where the session cookie flows same-origin).
        api: apiUrl('/api/chat'),
        headers: () => authHeaders(),
        resumable: { storage: resumeStorage, resumeApi: recovery.resumeApi },
        resumeCancellation,
        fetch: createBillingFetch(
          recovery.fetch,
          () => billingGuardRef.current.questionGate.allow(),
          () => billingGuardRef.current.billing?.reject(),
        ),
        // Auto mode is the DEFAULT: the model discovers capabilities on demand
        // via find_right_tools (scales to 1000s of packs — the whole catalog is
        // never in context). Manually checking packs in the sidebar is the
        // debug/advanced mode that pins the toolset instead.
        // timezone/locale: the model's "current time" line renders in the
        // user's saved company-workspace zone when set, otherwise the browser
        // zone. The server remains the source of truth for signed-in settings.
        // `viewport` is the rendering-surface hint: narrow (phone-width)
        // viewports get a compact-formatting system note server-side.
        // The body is read per send, so only a voice-origin turn carries `voice`.
        body: () =>
          chatRequestBody({
            threadId,
            packIds: selected.size ? [...selected] : ['auto'],
            // Local-only model override (ui/components/model-picker.tsx).
            model: (__PONEVA_DEV_TOOLS__ && readLocalModel()) || null,
            timezone: settings.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: replyLocale,
            narrow: narrowViewport,
            voice: currentVoiceTurnBody(),
            personality: settings.personality ?? null,
          }),
      },
      guardSubmittedTurn(
        () => billingGuardRef.current.questionGate.allow(),
        async (messages) => {
          resumeStorage.preventDiscovery?.();
          requestHistoryOwnerRef.current = isCurrentHistoryOwner;
          finishedTurnRef.current = rememberFinishedTurn;
          await rememberSubmittedTurn(messages);
          if (visibleMount.current && requestHistoryOwnerRef.current())
            threadSavedRef.current(threadId);
        },
      ),
      {
        context: () => workspaceRef.current?.turnContext,
        answerReaderAdmitted: () => answerReaderAdmitted,
        captureNativeSelection: () => workspaceRef.current?.captureNativeSelection(threadId),
        captureEditingApp: () => workspaceRef.current?.captureEditingApp(threadId),
        unsent: (messageId, refused, code) => {
          if (refused) unsentTurns.add(messageId);
          else unsentTurns.delete(messageId);
          // Refused because the preview on show is gone: its Continue belongs to that message and that preview.
          workspaceRef.current?.noteGoneRefusal(
            threadId,
            messageId,
            refused && code === 'editing_preview_unavailable',
          );
        },
        // The owner asked a suggested follow-up: the turn carries its id.
        // Pending model choices also accompany retries, until the server admits them.
        // Queued follow-ups never approve an action; the first send after Stop
        // names the stopped stream so the server stops it before admission.
        extraBody: (trigger, message) => {
          const model = captureModelSelection(threadId);
          const body = {
            ...(trigger === 'submit-message' ? askedSuggestionBody(threadId) : null),
            ...model?.body,
            ...queuedOriginBody(message),
            ...replaceStreamBody(threadId),
          };
          return Object.keys(body).length ? { body, accepted: model?.accepted } : null;
        },
        // The chat starts building an App: where the canvas sits beside the chat, it opens at once on the build (or
        // the App the chat already has), the first time in this page session, with the rules of a finished preview.
        // Live builds follow inside it. Where it would cover the chat, the finished preview opens it as before.
        toolStart: (name, origin) => {
          const current = workspaceRef.current;
          if (
            passive ||
            origin.resumed ||
            !APP_AUTO_OPEN_TOOLS.has(name) ||
            window.matchMedia?.('(max-width: 1100px)').matches ||
            !current ||
            current.view !== 'chat' ||
            current.threadId !== threadId ||
            current.canvas?.kind === 'app-preview' ||
            appPreviewShown.current.has(threadId) ||
            !visibleMount.current ||
            !requestHistoryOwnerRef.current()
          )
            return;
          const known = readAppBuild(threadId);
          const opening = appPreviewOpening(
            {
              kind: 'app-preview',
              id: known?.latest ?? APP_BUILDING_ID,
              title: known?.title ?? '',
            },
            { sameThread: true, canvas: current.canvas, opened: false },
          );
          if (opening && current.openCanvas(opening.target, opening.options))
            appPreviewShown.current.add(threadId);
        },
        // The App selection belongs to the message that was just sent.
        submitted: (messageId) => {
          const current = workspaceRef.current;
          // What the composer showed as attached stays on the message that carries it (and in its history).
          if (!passive && messageId && current?.threadId === threadId)
            rememberMessageContext(threadId, messageId, turnContextChips(current, uiText));
          if (
            passive ||
            (!current?.turnContext.appSelection &&
              !current?.turnContext.storefrontSelection &&
              !current?.turnContext.storefrontConversations) ||
            current.threadId !== threadId
          )
            return;
          current.setTurnContext({
            ...current.turnContext,
            appSelection: undefined,
            storefrontSelection: undefined,
            storefrontConversations: undefined,
          });
        },
        event: (event) => {
          if (passive) return;
          if (visibleMount.current && requestHistoryOwnerRef.current())
            workspaceRef.current?.acceptEvent(
              event as Parameters<NonNullable<typeof workspace>['acceptEvent']>[0],
            );
        },
        artifact: (name, output, origin) => {
          // Settings belong to the account, not this thread, so this comes
          // before the pane's own checks (ui/lib/account-settings.tsx). App's
          // apply takes only a change made to the signed-in account and
          // re-reads the server for a resumed stream's replay.
          const settingsResult = routeSettingsArtifact(name, output, origin, {
            isOwner: () => !passive && requestHistoryOwnerRef.current(),
            apply: (change, at) => settingsChangedRef.current(change, at),
          });
          if (settingsResult) return;
          if (
            passive ||
            !visibleMount.current ||
            !requestHistoryOwnerRef.current() ||
            !output ||
            typeof output !== 'object'
          )
            return;
          const value = output as {
            status?: string;
            skill?: CompanySkill;
            result?: { id: string };
            agent?: { id: string };
          };
          const current = workspaceRef.current;
          if (!current || value.status === 'error') return;
          const preview = appPreviewTarget(name, output, threadId);
          if (preview && current.view === 'chat' && !origin.resumed) {
            const opening = appPreviewOpening(preview, {
              sameThread: current.threadId === threadId,
              canvas: current.canvas,
              opened: appPreviewShown.current.has(threadId),
            });
            if (opening) {
              if (current.openCanvas(opening.target, opening.options))
                appPreviewShown.current.add(threadId);
            }
            return;
          }
          if ((name === 'create_skill_draft' || name === 'update_skill_draft') && value.skill)
            current.acceptSkill(value.skill, { open: true });
          else if (name === 'save_work_result' && value.result && current.view === 'chat')
            current.acceptArtifact({ kind: 'result', id: value.result.id });
          else if (name === 'save_artifact') {
            // Only a committed receipt's own tuple opens the saved Artifact and refreshes the Library.
            const saved = committedSaveTarget(output);
            if (saved) {
              notifyLibraryChanged();
              if (current.view === 'chat') current.acceptArtifact(saved);
            }
          } else if (name === 'propose_company_knowledge' || name === 'collect_company_source')
            void current.refresh();
          else if (name === 'suggest_page_edits' || name === 'propose_knowledge_change') {
            announceKnowledgePagesChanged();
            // A suggestion made in this turn opens beside the chat, never over
            // other work, and not again when a resumed stream replays it.
            const target =
              value.status === 'pending_review' && !origin?.resumed
                ? suggestionCanvas(output)
                : null;
            if (target && current.view === 'chat') current.openCanvas(target, { auto: true });
          } else if (
            ['create_agent', 'update_agent', 'set_agent_trigger'].includes(name) &&
            value.agent
          )
            current.openCanvas({ kind: 'agent', id: value.agent.id });
          else if (name === 'project_context') {
            // Projects v1: the server placed this chat in its project (the first answer reads
            // the brief); from now on it reads the project from the thread, so the id is not
            // sent again. A refused placement never gets here: the turn's end lets it go.
            const placed =
              current.threadId === threadId ? current.turnContext.projectId : undefined;
            if (placed) {
              rememberThreadPlace(threadId, projectPlace(placed));
              const { projectId: _placed, ...rest } = current.turnContext;
              current.setTurnContext(rest);
            }
            if (current.threadId === threadId) current.refreshThreadMeta();
          } else if (name === 'draft_project') {
            // A drafted project opens beside the chat; Create posts its latest draft.
            rememberProjectDraft(threadId, output);
            if (current.view === 'chat' && current.threadId === threadId && !origin?.resumed)
              current.openCanvas({ kind: 'project-draft', id: threadId }, { auto: true });
          } else if (name === 'move_chat_to_project') {
            const place = (output as { project?: ProjectPlace | null }).project ?? null;
            if (place) rememberThreadPlace(threadId, place);
            if (current.threadId === threadId) current.refreshThreadMeta();
          } else if (name === 'change_storefront_design') {
            // A saved design moves an open storefront canvas to the new version, and opens it beside the chat
            // the first time, like an App's preview; a resumed stream only refreshes.
            const store = (output as { store?: { id?: unknown } }).store;
            if (value.status === 'ok' && typeof store?.id === 'string') {
              announceStorefrontDesign(store.id);
              if (current.view === 'chat' && !origin?.resumed)
                current.openCanvas({ kind: 'storefront', id: store.id }, { auto: true });
            }
          }
        },
        voiceToolTiming: (value, origin) => {
          if (passive || !visibleMount.current || !requestHistoryOwnerRef.current()) return;
          voiceController().onToolTiming(value, origin.resumed);
        },
        voiceOutput: (value, origin) => {
          if (passive || !visibleMount.current || !requestHistoryOwnerRef.current()) return;
          if (!origin.resumed) {
            const current = workspaceRef.current;
            const target = current
              ? voicePreviewTarget(value, threadId, current.threadId, origin.resumed)
              : null;
            if (current && current.view === 'chat' && target)
              current.openCanvas(target, { auto: true });
          }
          voiceController().onSpokenOutput(value, origin.resumed);
        },
      },
    );
  }, [
    selected,
    settings,
    threadId,
    replyLocale,
    narrowViewport,
    resumeStorage,
    resumeCancellation,
    signedIn,
    chatAttachments,
    passive,
    following,
    onThreadTitle,
    rememberSubmittedTurn,
    rememberFinishedTurn,
    isCurrentHistoryOwner,
    chatFetch,
    answerReaderAdmitted,
    unsentTurns,
  ]);
  // Seed the runtime with this thread's stored messages (the component is
  // remounted per threadId, so this runs on every thread switch); the history
  // adapter keeps persisting new messages as they arrive.
  const initialMessages = useMemo(() => {
    const messages = loadServerThreadMessages(threadId);
    if (!passive) discoverAnswerAtMount(resumeStorage, threadId, messages, answerReaderAdmitted);
    return messages;
  }, [threadId, passive, resumeStorage, answerReaderAdmitted]);
  const finishWorkspacePreview = () => {
    const current = workspaceRef.current;
    if (!current || !visibleMount.current || !requestHistoryOwnerRef.current()) return;
    const next = finishPreviewContext(current.turnContext);
    if (next !== current.turnContext) current.setTurnContext(next);
  };
  // Company workspaces read large documents from the Library instead.
  const tooLargeToAttach = (file: File) => {
    const current = workspaceRef.current;
    current?.notify(inlineAttachmentTooLarge(file.name));
    return Boolean(current);
  };
  const businessFileScope = useRef('');
  businessFileScope.current =
    userId && company.data?.company?.id ? `${userId}\0${company.data.company.id}` : '';
  const attachmentAdapter = useMemo(
    () =>
      new CompositeAttachmentAdapter([
        chatAttachments && signedIn && !passive
          ? new StoredChatImageAdapter({
              threadId,
              isCurrentOwner: isCurrentHistoryOwner,
              scope: () => businessFileScope.current,
            })
          : new DownscalingImageAttachmentAdapter(),
        new BusinessFileAttachmentAdapter({
          scope: () => businessFileScope.current,
          upload: uploadWorkspaceFile,
          place: () => attachProject(workspaceRef.current),
          onSaved: (saved, place) => {
            void workspaceRef.current?.refresh();
            notifyLibraryChanged();
            joinProject(place, saved, (message) =>
              workspaceRef.current?.notify(message, null, 'error'),
            );
          },
          onError: (message) => workspaceRef.current?.notify(message, null, 'error'),
          unreadable: (file) => fileReadabilityMessage(file as CompanyFile),
        }),
        new InlineSizeLimitAdapter(new PdfAttachmentAdapter(), tooLargeToAttach),
        new InlineSizeLimitAdapter(new PastedTextAttachmentAdapter(), tooLargeToAttach),
      ]),
    [chatAttachments, signedIn, passive, threadId, isCurrentHistoryOwner],
  );
  const runtime = useChatRuntime({
    messages: initialMessages,
    ...(chatAttachments ? { joinStrategy: 'none' as const } : {}),
    transport,
    // Completion belongs to the request, not the currently visible thread.
    // The SDK keeps this callback after New chat unmounts its history listener.
    onFinish: (event) =>
      completeWorkspaceTurn(event, {
        threadId,
        finishPreview: () => {
          // Only an answered turn shows the server left the chat unplaced; a failed, stopped or
          // dropped one may never have reached placement, and Try again can still place it.
          if (!event.isError && !event.isAbort && !event.isDisconnect)
            letUnplacedProjectGo(workspaceRef.current, threadId);
          finishWorkspacePreview();
        },
        // A follower never saves an answer: its owner's browser (or the
        // agent run) does.
        persistHistory: (finished) =>
          passive ? traceHistory(threadId, 'passive_follower') : finishedTurnRef.current(finished),
        isCurrentOwner: () => requestHistoryOwnerRef.current(),
        refreshWorkspace: () => workspaceRef.current?.refresh(),
      }),
    onError: finishWorkspacePreview,
    adapters: {
      history,
      attachments: attachmentAdapter,
    },
  });
  // Config may arrive after mount; bind the rebuilt transport to this same
  // native runtime before its first compact request (the existing6a fit).
  if (chatAttachments) transport.setRuntime(runtime);
  useInitialServerBranch(
    threadId,
    runtime.thread,
    chatAttachments && signedIn,
    isCurrentHistoryOwner,
  );

  // The native dynamic transport switches in an effect. Bind a rebuilt
  // transport now as well, before delayed reader admission starts its GET.
  transport.setRuntime(runtime);

  // Speech is the app's own session and recording state (ui/speech): partials
  // never enter the draft and committed text is inserted at the caret, so
  // assistant-ui's dictation adapter is not used.
  return (
    <SpeechProvider
      availability={speech}
      userId={userId}
      organizationId={company.data?.company?.id ?? null}
      threadId={threadId}
    >
      <ResumeCancellationContext.Provider value={cancelForStop}>
        <AssistantRuntimeProvider runtime={runtime}>
          <ActionTurnObserver threadId={threadId} />
          <ActivityScopeObserver
            ownerId={userId ?? ''}
            companyId={company.data?.company?.id ?? ''}
            threadId={threadId}
          />
          <AppBuildObserver threadId={threadId} />
          <StorefrontDesignObserver threadId={threadId} />
          {/* A follower has no composer to send from or resume into. */}
          {!passive && (
            <PendingAuthSend armed={signedIn && pendingSend} onConsumed={onPendingSendConsumed} />
          )}
          <FollowMerge
            threadId={threadId}
            enabled={
              passive || Boolean(workspace?.threadMeta?.agentRunId && !hasLocalStream(threadId))
            }
          />
          {!passive && (
            <ComposerResume
              draft={resumeDraft}
              sessionResolved={sessionResolved}
              signedIn={signedIn}
              billingReady={billing?.required !== null}
              onConsumed={onResumeConsumed}
            />
          )}
          <ComposerTextBridge
            register={registerComposerText}
            registerAttachments={registerComposerAttachments}
          />
          {!passive && <VoiceRoot threadId={threadId} />}
          <SteerProvider
            threadId={threadId}
            attachments={attachmentAdapter}
            onError={(error) => workspaceRef.current?.notify(error.message, null, 'error')}
            enabled={signedIn && !passive}
            userId={userId ?? undefined}
            companyId={company.data?.company?.id ?? undefined}
            sendBlocked={questionGate.needsAuth}
          >
            <main className="bg-background flex min-w-0 flex-1 flex-col">
              <div className="relative min-h-0 flex-1 pb-[calc(env(safe-area-inset-bottom)+var(--keyboard-inset,0px))] [transition:padding-bottom_120ms_ease-out]">
                <MessageRepairProvider
                  enabled={chatAttachments && signedIn && !passive}
                  threadId={threadId}
                  thread={runtime.thread}
                  attachments={attachmentAdapter}
                  isCurrentOwner={isCurrentHistoryOwner}
                >
                  <ThreadWithSuggestions
                    threadId={threadId}
                    replyLocale={replyLocale}
                    composerHighlights={composerHighlights}
                    share={share}
                  />
                </MessageRepairProvider>
              </div>
            </main>
          </SteerProvider>
        </AssistantRuntimeProvider>
      </ResumeCancellationContext.Provider>
    </SpeechProvider>
  );
}

// This browser has its own resumable stream for the chat (it sent the turn):
// it resumes that instead of following.
function hasLocalStream(threadId: string) {
  try {
    return Boolean(sessionStorage.getItem(`poneva.chat.resume.${threadId}`));
  } catch {
    return false;
  }
}

function App() {
  const [billingDraft, setBillingDraft] = useState(consumeBillingDraft);
  // Bumped by "Check again": the pane remounts and re-reads the original turn (never posts it again).
  const [checkAgain, setCheckAgain] = useState(0);
  const requestCheckAgain = useCallback(() => setCheckAgain((value) => value + 1), []);
  // The site/tool picker was removed from the sidebar; the assistant always runs
  // in Auto mode (no packs pinned), so this stays an empty set.
  const [selected] = useState<Set<string>>(new Set());
  const [threads, setThreads] = useState<ThreadMeta[]>([]);
  // A model-generated title arrives mid-stream on the opening turn (see
  // ChatPane's transport). A listed thread is renamed in drawer state at
  // once; a thread not listed yet (its first turn is still running) is added
  // by the run-end refresh, already titled — the same "no titled, message-
  // less row" rule the index and the server keep, so an abandoned opening
  // turn never leaves a ghost chat in the drawer either.
  const onThreadTitle = useCallback((id: string, title: string) => {
    setThreads((current) =>
      current.some((thread) => thread.id === id) ? withThreadTitle(current, id, title) : current,
    );
  }, []);
  const [threadId, setThreadId] = useState<string>(() => newThreadId());
  const themeState = useThemeChoice();
  const { theme } = themeState;
  // Company work is always server-backed. A null user is a signed-out session,
  // never a browser-owned chat store.
  const [user, setUser] = useState<SessionUser | null>(null);
  const sessionBoundary = useMemo(createSessionBoundary, []);
  const isCurrentHistoryOwner = sessionBoundary.capture(user?.id ?? null);
  // Settings are server-backed and scoped to the current signed-in account.
  const [settings, setSettings] = useState<Settings>(EMPTY_SETTINGS);
  const [billingRequired, setBillingRequired] = useState<boolean | null>(null);
  const [chatAttachments, setChatAttachments] = useState(false);
  const billing = useBillingState(billingRequired, user?.id ?? null, settings.timezone);
  const [composerHighlights, setComposerHighlights] = useState<ComposerHighlight[]>([]);
  const [speech, setSpeech] = useState<SpeechAvailability>(SPEECH_UNAVAILABLE);
  const [answerReaderAdmitted, setAnswerReaderAdmitted] = useState(false);
  // One-shot params from an OAuth round-trip (?auth_error, ?invite deep link)
  // plus the pre-redirect composer draft — consumed exactly once at mount.
  const [boot] = useState(() => ({
    ...consumeBootParams(),
    oauthRequest: stashOAuthRequest(),
    draft: billingDraft ? null : consumeOAuthDraft(),
  }));
  const [inviteCode, setInviteCode] = useState(boot.invite);
  const [authConfig, setAuthConfig] = useState<AuthConfig>({ oauth: [] });
  // Returning from a FAILED redirect: reopen the auth screen with the error,
  // re-arm the pending send if one was in flight, and restore (but don't send)
  // the draft. A successful return instead sends it once the session resolves.
  const [authErrorKey, setAuthErrorKey] = useState(boot.authError);
  const [authOpen, setAuthOpen] = useState(() =>
    Boolean(boot.authError || boot.verify || boot.reset || boot.invite || boot.oauthRequest),
  );
  // A returning person lands on Log in (with a Create account switch); an invitation link
  // opens its own sign-up.
  const [authInitialScreen, setAuthInitialScreen] = useState<'signup' | 'login'>(
    boot.invite ? 'signup' : 'login',
  );
  const [passwordResetToken, setPasswordResetToken] = useState(boot.reset);
  const [pendingSend, setPendingSend] = useState(() => Boolean(boot.authError && boot.draft?.send));
  const [resumeDraft, setResumeDraft] = useState(() =>
    boot.draft ? { text: boot.draft.text, send: boot.draft.send && !boot.authError } : null,
  );
  const [sessionResolved, setSessionResolved] = useState(false);
  // Another tab can replace this browser's session; then this tab stops and says so.
  const sessionGuardState = useSessionGuard(user?.id);
  const sessionChange = sessionGuardState.changed
    ? (sessionGuardState.current ?? {
        userId: 'unknown',
        companyId: null,
        account: null,
        company: null,
      })
    : null;
  const [verificationBusy, setVerificationBusy] = useState(Boolean(boot.verify));
  // Verification link opened outside the signup browser: hold the emailed
  // token while the auth screen asks for the chosen password (the second
  // completion proof when the HttpOnly pending cookie is absent).
  const [verifyConfirmToken, setVerifyConfirmToken] = useState<string | null>(null);
  const composerTextRef = useRef<() => string>(() => '');
  const composerAttachmentsRef = useRef<() => boolean>(() => false);
  const chatNavigation = useChatNavigation({
    enabled: true,
    ready: sessionResolved,
    userId: user?.id,
    threadId,
    selectThread: setThreadId,
    isCurrentOwner: isCurrentHistoryOwner,
  });

  // Collaboration v1: the open chat's sharing and this person's access to it.
  const threadMeta = chatNavigation.meta;
  const openMeta = threadMeta?.id === threadId ? threadMeta : null;
  const readOnly = openMeta?.access === 'viewer';
  // Readers of a shared chat (and the owner of a run chat) watch it: its
  // state is polled, and a running turn is followed live (lib/chat-follow.ts).
  const follow = useChatFollow({
    threadId,
    meta: openMeta,
    enabled: Boolean(user),
    refresh: () => seedServerThread(threadId, isCurrentHistoryOwner),
  });
  // The owner watches their own agent run live, composer disabled, while it
  // runs and this browser has no stream of its own for it (PR 4).
  const watchingRun = Boolean(
    openMeta?.access === 'owner' &&
    openMeta.agentRunId &&
    follow.live?.active &&
    !hasLocalStream(threadId),
  );
  const following = readOnly || watchingRun;
  follow.followingRef.current = following;
  const { disarm } = follow;
  useEffect(() => {
    // A new follow mount attaches by itself.
    if (following) disarm();
  }, [following, disarm]);

  const adoptSession = (me: SessionUser | null, preserveDraft = false, accountDeleted = false) => {
    const wasResolved = sessionBoundary.userId !== undefined;
    const previousUserId = sessionBoundary.userId;
    let rememberedOwner: string | null = null;
    try {
      rememberedOwner = localStorage.getItem(LAST_SESSION_OWNER_KEY);
    } catch {
      /* storage disabled */
    }
    // A tab can disappear or its session can expire without running the
    // sign-out path. Meeting takes remain recoverable; this only clears the
    // previous owner's kept dictation audio when the session expires or another account arrives.
    const ownerToPurge = rememberedOwnerToPurge(rememberedOwner, me?.id ?? null, previousUserId);
    if (ownerToPurge) void purgeRememberedDictation(ownerToPurge, speechSpool).catch(() => {});
    if (sessionBoundary.transition(me?.id ?? null)) {
      // Speech belongs to one signed-in session: the meeting recorder stops
      // for the previous account and its spooled dictation audio is deleted;
      // unsent meeting audio stays for that account's next sign-in here.
      if (previousUserId)
        void releaseSignedOutSpeech(previousUserId, { accountDeleted }).catch(() => {});
      if (accountDeleted) {
        try {
          localStorage.removeItem(LAST_SESSION_OWNER_KEY);
        } catch {
          /* storage disabled */
        }
      }
      setThreads([]);
      setSettings(EMPTY_SETTINGS);
      // Project names this tab saw belong to the account that saw them.
      forgetProjects();
      if (!preserveDraft) {
        setPendingSend(false);
        setResumeDraft(null);
        composerTextRef.current = () => '';
        composerAttachmentsRef.current = () => false;
        if (!wasResolved) setThreadId(newThreadId());
        else if (!(me && keepsLinkedChatOnSignIn(previousUserId, location.hash)))
          chatNavigation.fresh();
      }
    }
    setUser(me);
    if (me) persist(LAST_SESSION_OWNER_KEY, me.id);
    // An agent app is waiting for this person's consent: continue there.
    if (me) resumeOAuthRequest();
  };

  const discardInvite = () => {
    clearInvite();
    setInviteCode(null);
  };

  // Settings changed by asking, Undo, and re-reading them when the page is
  // visible again (ui/lib/account-settings.tsx).
  const accountSettings = useAccountSettingsControl({
    userId: user?.id ?? null,
    settings,
    setSettings,
    theme: themeState,
    isCurrent: (id) => sessionBoundary.capture(id),
  });

  // Adopt saved language, timezone, and personalization preferences. A server
  // theme applies here; null keeps this device's own choice.
  const adoptSettings = accountSettings.adopt;

  // Protected work for a freshly adopted account (its settings, thread list, a returning billing draft) runs
  // only after that account's UI has COMMITTED (a cleared/loading shell), its sole guard is installed, and the
  // coherent bootstrap says the tab and the server agree. Until then the previously shown account, if any, is
  // still on screen with its own guard, and nothing is dispatched or materialized for the new one. One slot
  // per kind; the guard and generation captured when the work becomes runnable are checked again before it
  // materializes, and work for an account that never commits is dropped.
  const readyFor = useRef<{ userId: string; guard: SessionGuard; generation: number } | null>(null);
  const protectedWork = useRef(
    new Map<string, { userId: string; run: () => void | Promise<void> }>(),
  );
  type ReadyStamp = NonNullable<typeof readyFor.current>;
  const stillReady = (stamp: ReadyStamp | null) =>
    stamp !== null &&
    readyFor.current === stamp &&
    !stamp.guard.changed &&
    stamp.guard.generation === stamp.generation;
  const runProtected = (userId: string, kind: string, run: () => void | Promise<void>) => {
    const ready = readyFor.current;
    if (
      ready &&
      ready.userId === userId &&
      !ready.guard.changed &&
      ready.guard.generation === ready.generation
    )
      void run();
    else protectedWork.current.set(kind, { userId, run });
  };
  const guardForWork = sessionGuardState.handle;
  useEffect(() => {
    readyFor.current = null;
    if (!guardForWork || !user) {
      protectedWork.current.clear();
      return;
    }
    const { guard } = guardForWork;
    const userId = user.id;
    const generation = guard.generation;
    let live = true;
    void guard.ready().then((verdict) => {
      if (!live || verdict !== 'same' || guard.changed || guard.generation !== generation) return;
      readyFor.current = { userId, guard, generation };
      for (const [kind, entry] of [...protectedWork.current]) {
        if (entry.userId === userId) {
          protectedWork.current.delete(kind);
          void entry.run();
        } else if (entry.userId !== sessionBoundary.userId) {
          // Neither the committed account nor the latest adopted one: retired or superseded, dropped. Work
          // for an adopted account that has not committed yet (a pending Suspense commit) is KEPT.
          protectedWork.current.delete(kind);
        }
      }
    });
    return () => {
      live = false;
      if (readyFor.current?.guard === guard) readyFor.current = null;
    };
  }, [guardForWork, user?.id]);

  // Hydrate the current account's server row; signed-out state has no
  // account-owned settings to restore.
  const hydrateSettings = (asUser: SessionUser | null) => {
    const isCurrent = sessionBoundary.capture(asUser?.id ?? null);
    if (!isCurrent()) return;
    if (asUser) {
      runProtected(asUser.id, 'settings', () => {
        const stamp = readyFor.current;
        void fetchServerSettings().then((s) => {
          if (isCurrent() && stillReady(stamp)) adoptSettings(s ?? EMPTY_SETTINGS);
        });
      });
    } else adoptSettings(EMPTY_SETTINGS);
  };

  // Choose light, dark or system; it is saved and follows the person to
  // every device.
  const chooseTheme = accountSettings.choose;

  // Load the server thread list for the current account. Signed-out state has
  // no local history fallback.
  const refreshThreads = (asUser: SessionUser | null = user) => {
    const isCurrent = sessionBoundary.capture(asUser?.id ?? null);
    if (!isCurrent()) return;
    if (asUser) {
      runProtected(asUser.id, 'threads', () => {
        const stamp = readyFor.current;
        void listServerThreads()
          .then((rows) => {
            if (isCurrent() && stillReady(stamp)) setThreads(rows);
          })
          .catch(() => {
            if (isCurrent() && stillReady(stamp)) setThreads([]);
          });
      });
    } else setThreads([]);
  };

  // Email clients may reuse an already-open app tab. A fragment-only
  // navigation does not remount the SPA, so consume hash changes explicitly
  // as well as the cold-boot path below.
  useEffect(() => {
    const completeFragmentVerification = () => {
      const reset = consumeAuthFragment('reset');
      if (reset) {
        setPasswordResetToken(reset);
        setAuthErrorKey(null);
        setAuthOpen(true);
        return;
      }
      const token = consumeAuthFragment('verify');
      if (!token) return;
      setVerificationBusy(true);
      setAuthErrorKey(null);
      setAuthOpen(true);
      void completePasswordSignup(token)
        .then((me) => {
          adoptSession(me);
          setAuthOpen(false);
          setSessionResolved(true);
          hydrateSettings(me);
          refreshThreads(me);
        })
        .catch((error) => {
          const code = error instanceof AuthError ? error.code : 'server_unavailable';
          if (code === 'verification_password_required') {
            setVerifyConfirmToken(token);
          } else {
            setAuthErrorKey(code);
          }
          setAuthOpen(true);
        })
        .finally(() => setVerificationBusy(false));
    };
    window.addEventListener('hashchange', completeFragmentVerification);
    return () => window.removeEventListener('hashchange', completeFragmentVerification);
  }, []);

  // Warm Android OAuth returns arrive through Capacitor's appUrlOpen event.
  // Cold-launch callbacks are consumed inside the boot sequence below so they
  // cannot race the initial signed-out session resolution.
  useEffect(() => {
    if (!isAndroidNativeShell()) return;
    let dispose: (() => Promise<void>) | null = null;
    void listenForNativeGoogleCallback(async (callback) => {
      if ('error' in callback) {
        setAuthErrorKey(callback.error);
        setAuthOpen(true);
        return;
      }
      try {
        const me = await completeNativeGoogleSignIn(callback.code, callback.verifier);
        await clearNativeGoogleVerifier();
        adoptSession(me);
        setAuthOpen(false);
        setAuthErrorKey(null);
        setSessionResolved(true);
        hydrateSettings(me);
        refreshThreads(me);
      } catch (error) {
        setAuthErrorKey(error instanceof AuthError ? error.code : 'server_unavailable');
        setAuthOpen(true);
      }
    })
      .then((cleanup) => {
        dispose = cleanup;
      })
      .catch(() => {
        setAuthErrorKey('server_unavailable');
        setAuthOpen(true);
      });
    return () => {
      void dispose?.();
    };
  }, []);

  useEffect(() => {
    const isBootCurrent = sessionBoundary.capture();
    void fetchConfig().then(
      ({ billingRequired, composerHighlights, speech, answerReaderAdmitted, chatAttachments }) => {
        setBillingRequired(billingRequired);
        setComposerHighlights(composerHighlights);
        setSpeech(speech);
        setAnswerReaderAdmitted(answerReaderAdmitted);
        setChatAttachments(chatAttachments);
      },
    );
    // Which sign-in surfaces to render (OAuth providers and invite admission).
    const authDiscovery = fetchAuthConfig().then((config) => {
      setAuthConfig(config);
    });
    // Load the shell's persisted bearer token (web no-op), resolve the
    // session, then hydrate settings and the matching thread list.
    void authDiscovery
      .then(() => initSessionToken())
      .then(async () => {
        const nativeCallback = await getNativeGoogleLaunchCallback();
        if (nativeCallback) {
          if ('error' in nativeCallback) {
            return { me: null, verificationError: nativeCallback.error, nativeGoogle: true };
          }
          try {
            const me = await completeNativeGoogleSignIn(
              nativeCallback.code,
              nativeCallback.verifier,
            );
            await clearNativeGoogleVerifier();
            return { me, verificationError: null, nativeGoogle: true };
          } catch (error) {
            return {
              me: null,
              verificationError: error instanceof AuthError ? error.code : 'server_unavailable',
              nativeGoogle: true,
            };
          }
        }
        if (boot.verify) {
          try {
            return {
              me: await completePasswordSignup(boot.verify),
              verificationError: null,
              nativeGoogle: false,
            };
          } catch (error) {
            return {
              me: null,
              verificationError: error instanceof AuthError ? error.code : 'server_unavailable',
              nativeGoogle: false,
            };
          }
        }
        const me = boot.authComplete === 'google' ? await resumeOAuthSession() : await fetchMe();
        return { me, verificationError: null, nativeGoogle: false };
      })
      .then(async ({ me, verificationError, nativeGoogle }) => {
        if (!isBootCurrent()) return;
        setVerificationBusy(false);
        if (verificationError === 'verification_password_required' && boot.verify) {
          setVerifyConfirmToken(boot.verify);
          setAuthOpen(true);
        } else if (verificationError) {
          setAuthErrorKey(verificationError);
          setAuthOpen(true);
        }
        if (me && boot.verify) {
          setAuthOpen(false);
        }
        if (me && boot.invite) {
          // The company provider validates and accepts this recipient's invite.
          setAuthOpen(false);
        }
        if (me && nativeGoogle) {
          setAuthOpen(false);
        }
        adoptSession(me, Boolean(boot.draft));
        const isCurrent = sessionBoundary.capture(me?.id ?? null);
        const returningDraft = billingDraftForUser(billingDraft, me?.id ?? null);
        setBillingDraft(null);
        setSessionResolved(true);
        hydrateSettings(me);
        if (me) {
          // Protected: after this account's UI has committed and its guard is coherent-ready (see runProtected).
          runProtected(me.id, 'boot', async () => {
            const stamp = readyFor.current;
            // seedServerThread checks this before and after its request and writes its caches only if it
            // still holds: the session boundary AND this account's captured guard/generation.
            const stillOwner = () => isCurrent() && stillReady(stamp);
            if (returningDraft) {
              const seeded = await seedServerThread(returningDraft.threadId, stillOwner)
                .then(() => true)
                .catch(() => false);
              if (!stillOwner()) return;
              if (seeded) {
                setThreadId(returningDraft.threadId);
                writeWorkspaceHash(chatHash(returningDraft.threadId), 'replace');
              }
              setResumeDraft({ text: returningDraft.text, send: false });
            }
            await listServerThreads()
              .then((server) => {
                if (!isCurrent() || !stillReady(stamp)) return;
                setThreads(server);
              })
              .catch(() => {
                if (isCurrent() && stillReady(stamp)) setThreads([]);
              });
          });
        }
      });
  }, []);

  // Returns the conversation it started: a caller that writes into the new
  // thread (Skill authoring) needs its id before the state change lands.
  const startNewChat = () => {
    const fresh = chatNavigation.fresh();
    setResumeDraft(null);
    setPendingSend(false);
    refreshThreads();
    return fresh;
  };
  const openThread = (id: string) => chatNavigation.open(id);

  const onAuthed = (me: SessionUser) => {
    adoptSession(me);
    setSessionResolved(true);
    setAuthOpen(false);
    setAuthErrorKey(null);
    refreshThreads(me);
    hydrateSettings(me);
  };
  // Finish cross-device verification: the emailed token plus the chosen
  // password create the account and sign in here. Failures propagate to the
  // confirm screen, which renders them localized.
  const confirmVerify = verifyConfirmToken
    ? async (password: string) => {
        const me = await completePasswordSignup(verifyConfirmToken, password);
        setVerifyConfirmToken(null);
        onAuthed(me);
      }
    : null;
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const googleAttempt = useGoogleSignInAttempt();
  const signOut = () => {
    // Retire callbacks before the logout request settles. No old run may
    // mirror a late answer using a subsequently adopted account's cookie.
    adoptSession(null);
    setVerificationBusy(true);
    void serverLogout()
      // A native token that stayed in storage would sign this device in again at the next start: say so.
      .catch((error) =>
        setAuthErrorKey(
          error instanceof SessionTokenKeptError ? 'logout_incomplete' : 'server_unavailable',
        ),
      )
      .finally(() => {
        setVerificationBusy(false);
        setAuthInitialScreen('login');
        setAuthOpen(true);
      });
  };
  // Signing out stops a meeting this device records or still uploads: ask first.
  const onLogout = () => (meetingRecordingInProgress() ? setConfirmSignOut(true) : signOut());

  // The server deletion has committed before this callback runs. Retire the
  // signed-in session and all account-scoped UI state together.
  const onAccountDeleted = () => {
    adoptSession(null, false, true);
    setThreads([]);
    setThreadId(newThreadId());
    setSettings(EMPTY_SETTINGS);
    setPendingSend(false);
  };

  // Composer send-gate: a signed-out user tried to send. Arm the pending send
  // and open the sign-in view; if they close it without signing in, disarm so a
  // later real send isn't hijacked.
  const requestAuth = () => {
    setPendingSend(true);
    setAuthInitialScreen('signup');
    setAuthOpen(true);
  };
  const closeAuth = () => {
    setAuthOpen(false);
    setAuthErrorKey(null);
    setVerifyConfirmToken(null);
    setPasswordResetToken(null);
    if (!user) setPendingSend(false);
  };
  // Web OAuth unloads the page. Android keeps the SPA alive and opens Google
  // in a system-owned Custom Tab, then returns through a PKCE-bound deep link.
  const startGoogle = (companyName?: string) => {
    const desktop = desktopBridge();
    if (desktop) {
      void googleAttempt
        .run(async () =>
          desktop
            .signInWithGoogle({ inviteCode: inviteCode ?? undefined, companyName })
            .then(async () => {
              const me = await fetchMe();
              adoptSession(me);
              setAuthOpen(false);
              setAuthErrorKey(null);
              setSessionResolved(true);
              hydrateSettings(me);
              refreshThreads(me);
            }),
        )
        .catch((error) =>
          setAuthErrorKey(error instanceof AuthError ? error.code : 'server_unavailable'),
        );
      return;
    }
    if (!isAndroidNativeShell()) {
      stashOAuthDraft(composerTextRef.current(), pendingSend);
      void beginGoogleSignIn(inviteCode ?? undefined, companyName)
        .then((url) => location.assign(url))
        .catch((error) =>
          setAuthErrorKey(error instanceof AuthError ? error.code : 'server_unavailable'),
        );
      return;
    }
    void prepareNativeGoogleSignIn()
      .then(async ({ challenge }) => {
        const url = await beginNativeGoogleSignIn(challenge, inviteCode ?? undefined, companyName);
        await openNativeGoogleSignIn(url);
      })
      .catch((error) =>
        setAuthErrorKey(error instanceof AuthError ? error.code : 'server_unavailable'),
      );
  };
  // Keep Google hidden on iOS until Sign in with Apple parity is designed.
  const oauthProviders = oauthProvidersForCurrentPlatform(authConfig.oauth);
  const authGate = { needsAuth: !user, requestAuth };
  const beforeBillingRedirect = () => {
    if (!user) throw new Error('not_authenticated');
    if (billing.hasActiveEdit()) throw new Error('billing_edit_active');
    stashBillingDraft(
      composerTextRef.current(),
      threadId,
      user.id,
      composerAttachmentsRef.current(),
    );
  };
  // The saved app language owns every interface surface. The same locale is a
  // date/format hint. Nobody picks a dictation language: engines detect it,
  // with the company's languages as silent server-side hints, never the
  // interface locale, and the assistant follows the language of each user turn.
  const { interfaceLocale, replyLocale, authLocale } = resolveAppLocales(
    settings.language,
    navigator.language,
  );
  // A password change or Log out everywhere has ended every session, this one included: show login.
  const onSessionsEnded = () => {
    if (!isCurrentHistoryOwner()) return;
    adoptSession(null);
    setAuthInitialScreen('login');
    setAuthOpen(true);
  };
  const settingsPage = (
    <CompanySettingsPage
      key={sessionBoundary.generation}
      beforeBillingRedirect={beforeBillingRedirect}
      user={user}
      themeChoice={themeState.choice}
      onChooseTheme={chooseTheme}
      onSaved={(value) => {
        if (isCurrentHistoryOwner()) adoptSettings(value);
      }}
      onAccountDeleted={() => {
        if (isCurrentHistoryOwner()) onAccountDeleted();
      }}
      onPasswordChanged={onSessionsEnded}
      onLoggedOutEverywhere={onSessionsEnded}
    />
  );

  if (sessionChange)
    return (
      <ChatI18nProvider locale={interfaceLocale}>
        <SessionChangedNotice
          current={sessionChange}
          previous={user?.displayName ?? user?.email ?? null}
          onReload={() => location.reload()}
          onSignInAsPrevious={() => {
            void serverLogout()
              .catch(() => {})
              .finally(() => location.reload());
          }}
        />
      </ChatI18nProvider>
    );

  return (
    <ChatI18nProvider locale={interfaceLocale}>
      <TooltipProvider>
        <SessionGuardProvider value={sessionGuardState.handle}>
          <AuthGateContext.Provider value={authGate}>
            <CompanyProvider
              key={sessionBoundary.generation}
              userId={user?.id}
              inviteToken={inviteCode}
              onInviteAccepted={discardInvite}
            >
              <BillingContext.Provider
                value={{ ...billing, beforeRedirect: beforeBillingRedirect }}
              >
                <WorkspaceProvider
                  enabled
                  artifactCanvas={SavedArtifactCanvasRenderer}
                  threadMeta={threadMeta}
                  refreshThreadMeta={chatNavigation.refreshMeta}
                  threadLive={follow.live}
                  threadGone={follow.gone}
                  onThreadsChanged={() => refreshThreads()}
                  user={user}
                  threadId={threadId}
                  chatLinkId={chatNavigation.linkId}
                  chatStatus={chatNavigation.status}
                  retryChat={chatNavigation.retry}
                  threads={threads}
                  onNewChat={startNewChat}
                  onOpenThread={openThread}
                  onDraft={(text, _context, send) => setResumeDraft({ text, send })}
                  hasComposerDraft={() =>
                    Boolean(composerTextRef.current().trim()) || composerAttachmentsRef.current()
                  }
                >
                  <div
                    className={(theme === 'dark' ? 'dark ' : '') + 'flex h-dvh overflow-hidden'}
                    data-testid="app-shell"
                  >
                    <CompanyChatSurface signedIn={Boolean(user)}>
                      <WorkspaceShell settings={settingsPage} onLogout={onLogout}>
                        {/* A thread change remounts its runtime and composer state. */}
                        {/* A follower's pane remounts to attach to each new turn. */}
                        <ChatCheckAgainContext.Provider
                          value={
                            user && !readOnly && !following
                              ? {
                                  ownerId: user.id,
                                  checkAgain: () => {
                                    if (isCurrentHistoryOwner()) requestCheckAgain();
                                  },
                                }
                              : null
                          }
                        >
                          <ChatPane
                            chatAttachments={chatAttachments}
                            key={`${threadId}:${[...selected].sort().join(',')}:${readOnly ? 'read' : following ? 'follow' : 'write'}:${following ? follow.attach : 0}:${checkAgain}`}
                            selected={selected}
                            composerHighlights={composerHighlights}
                            answerReaderAdmitted={answerReaderAdmitted}
                            threadId={threadId}
                            readOnly={readOnly}
                            following={following}
                            onFollowOutcome={follow.report}
                            onFollowRetryAfter={follow.retryAfter}
                            signedIn={Boolean(user)}
                            isCurrentHistoryOwner={isCurrentHistoryOwner}
                            settings={settings}
                            replyLocale={replyLocale}
                            onHistoryChange={() => refreshThreads()}
                            onThreadSaved={chatNavigation.saved}
                            onThreadTitle={onThreadTitle}
                            pendingSend={pendingSend}
                            onPendingSendConsumed={() => setPendingSend(false)}
                            resumeDraft={resumeDraft}
                            sessionResolved={sessionResolved}
                            onResumeConsumed={() => setResumeDraft(null)}
                            registerComposerText={(fn) => {
                              composerTextRef.current = fn;
                            }}
                            registerComposerAttachments={(fn) => {
                              composerAttachmentsRef.current = fn;
                            }}
                            speech={speech}
                            userId={user?.id ?? null}
                            onSettingsChanged={accountSettings.apply}
                          />
                        </ChatCheckAgainContext.Provider>
                      </WorkspaceShell>
                    </CompanyChatSurface>
                    <CompanyWelcome
                      signedIn={Boolean(user)}
                      firstName={user?.displayName?.split(' ')[0]}
                      onStarted={(prompt) => {
                        startNewChat();
                        if (prompt) setResumeDraft({ text: prompt, send: true });
                      }}
                      onLogout={onLogout}
                    />
                    <CompanyBillingDialog beforeRedirect={beforeBillingRedirect} />
                    <SignOutWhileRecordingDialog
                      open={confirmSignOut}
                      onCancel={() => setConfirmSignOut(false)}
                      onSignOut={() => {
                        setConfirmSignOut(false);
                        signOut();
                      }}
                    />
                    {/* Session adoption remounts this subtree; only offer editable
                      credentials after the initial session has resolved. */}
                    <AuthScreen
                      open={sessionResolved && (!user || authOpen)}
                      onClose={user ? closeAuth : undefined}
                      onAuthed={onAuthed}
                      providers={oauthProviders}
                      verifying={verificationBusy && !authErrorKey}
                      confirmVerify={confirmVerify}
                      initialError={authErrorKey}
                      locale={authLocale}
                      inviteCode={inviteCode}
                      onGoogle={startGoogle}
                      googlePending={googleAttempt.pending}
                      resetToken={passwordResetToken}
                      initialScreen={authInitialScreen}
                      onPasswordReset={() => {
                        adoptSession(null);
                      }}
                    />
                  </div>
                </WorkspaceProvider>
              </BillingContext.Provider>
            </CompanyProvider>
          </AuthGateContext.Provider>
        </SessionGuardProvider>
      </TooltipProvider>
    </ChatI18nProvider>
  );
}

// index.html historically ships <html class="dark">; sync the class with the
// persisted theme before first paint of the app (light is the default).
document.documentElement.classList.toggle(
  'dark',
  resolveTheme(loadThemeChoice(), systemPrefersDark()) === 'dark',
);

// The states gallery (A09) exists only in the --dev-tools build: the define folds
// this to null in shipping bundles, so the dynamic import is never followed.
const StatesGallery = __PONEVA_DEV_TOOLS__
  ? lazy(() => import('@/dev/states/gallery').then((m) => ({ default: m.StatesGallery })))
  : null;

createRoot(document.getElementById('root')!).render(
  StatesGallery && location.hash.startsWith('#/dev/states') ? (
    <Suspense fallback={null}>
      <StatesGallery />
    </Suspense>
  ) : location.pathname === '/connection-return' ? (
    <ConnectionReturn />
  ) : (
    <App />
  ),
);
